"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.externalResetFields = exports.externalResetChildForm = exports.triggerEvent = exports.externalModelUpdate = exports.externalRunBinds = exports.runBinds = exports.updateValueAndRun = void 0;
/* eslint-disable default-case */
const immer_1 = require("immer");
const functional_1 = require("../utils/functional");
const set_data_1 = require("../utils/set-data");
const update_model_1 = require("../utils/update-model");
const resolvers_1 = require("../utils/resolvers");
const binds_1 = __importDefault(require("../handlers/binds"));
const handle_action_1 = __importDefault(require("../handlers/handle-action"));
const selectors_1 = require("./selectors");
const reporter_1 = __importDefault(require("../reporter"));
const extract_data_1 = require("../utils/extract-data");
// =======
// ACTIONS
// =======
const constants_1 = require("../constants");
// ===============
// ACTION CREATORS
// ===============
const updateValueAndRun = ({ value, formId, formIndex, modelRef, normalizer, repeatIndex, id }) => (dispatch, getState) => {
    const state = getState();
    if (state.rex.version[formId] > 1) {
        let termName = id;
        let iterableName;
        const ref = id.split('_');
        if (ref.length === 3) {
            [iterableName, , termName] = ref;
        }
        dispatch({
            type: 'FormModel/TERM_INPUT',
            payload: {
                modelKey: formId,
                termName,
                termValue: value,
                iterableName,
                iterableIndex: repeatIndex - 1,
            },
        });
        return;
    }
    const allBinds = state.rex.forms[formId].map(({ binds }) => binds);
    dispatch({
        type: constants_1.UPDATE_VALUE_AND_RUN,
        allBinds,
        value,
        formId,
        formIndex,
        modelRef,
        normalizer,
        repeatIndex,
    });
};
exports.updateValueAndRun = updateValueAndRun;
const runBinds = ({ formId, formIndex }) => (dispatch, getState) => {
    const { rex } = getState();
    const { binds } = rex.forms[formId][formIndex];
    dispatch({
        type: constants_1.RUN_UPDATES,
        formId,
        binds,
    });
};
exports.runBinds = runBinds;
const externalRunBinds = (useForm) => (dispatch, getState) => {
    const state = getState();
    const { id, index } = (0, selectors_1.useFormOrCurrent)(state, { useForm });
    if (state.rex.version[id] > 1) {
        return;
    }
    const { binds } = state.rex.forms[id][index];
    dispatch({
        type: constants_1.RUN_UPDATES,
        formId: id,
        binds,
    });
};
exports.externalRunBinds = externalRunBinds;
const externalModelUpdate = ({ data, formId, elementInjections }) => (dispatch, getState) => {
    const { rex } = getState();
    const allBinds = rex.forms[formId].map((form) => form.binds || []);
    dispatch({
        type: constants_1.EXTERNAL_MODEL_UPDATE,
        data,
        formId,
        allBinds,
        elementInjections,
    });
};
exports.externalModelUpdate = externalModelUpdate;
const triggerEvent = ({ eventName, useForm }) => (dispatch, getState) => {
    const state = getState();
    const { id, index } = (0, selectors_1.useFormOrCurrent)(state, { useForm });
    if (state.rex.version[id] > 1) {
        return;
    }
    const { [`event:${eventName}`]: actions, binds } = state.rex.forms[id][index];
    if (!actions) {
        reporter_1.default.warn(`The event, "${eventName}, was not found in this form: ${id} index ${index}`);
        return;
    }
    dispatch({
        type: constants_1.TRIGGER_EVENT,
        actions,
        binds,
        formId: id,
    });
};
exports.triggerEvent = triggerEvent;
const resetChildForm = ({ formId, formIndex }) => ({
    type: constants_1.RESET_CHILD_FORM,
    formId,
    formIndex,
});
const externalResetChildForm = (useForm) => (dispatch, getState) => {
    const state = getState();
    const { id, index } = (0, selectors_1.useFormOrCurrent)(state, { useForm });
    if (state.rex.version[id] > 1) {
        return;
    }
    dispatch(resetChildForm({ formId: id, formIndex: index }));
};
exports.externalResetChildForm = externalResetChildForm;
const resetFields = ({ formId, formIndex, fields }) => ({
    type: constants_1.RESET_FIELDS,
    formId,
    formIndex,
    fields,
});
const externalResetFields = ({ useForm, fields }) => (dispatch, getState) => {
    const state = getState();
    const { id, index } = (0, selectors_1.useFormOrCurrent)(state, { useForm });
    if (state.rex.version[id] > 1) {
        return;
    }
    dispatch(resetFields({ formId: id, formIndex: index, fields }));
};
exports.externalResetFields = externalResetFields;
// =======
// REDUCER
// =======
const modelReducer = (draft, action) => {
    switch (action.type) {
        case constants_1.LOAD_NEW_FORMS: {
            draft[action.id] = action.models;
            return;
        }
        case 'FormModel/LOAD_FORM': {
            draft[action.payload.modelKey] = [{}];
            return;
        }
        case constants_1.UPDATE_VALUE_AND_RUN: {
            const { allBinds, formId, formIndex, repeatIndex, value, modelRef, normalizer, } = action;
            const models = draft[formId];
            const fieldModelIds = (0, resolvers_1.resolveModelIds)({ modelRef, repeatIndex, models });
            const updates = (0, extract_data_1.extractUpdates)({
                models,
                modelIds: fieldModelIds,
            });
            const updatesModelIds = (0, extract_data_1.extractModelIdsFromActions)({
                models,
                binds: allBinds[formIndex],
                actions: updates,
                repeatIndex,
            });
            const bindIndexes = (0, extract_data_1.extractBindIndexes)({
                modelIds: [...fieldModelIds, ...updatesModelIds],
                models,
                // We need to use legacy sort for binding products
                // Agent Portal uses the formId of "edit-quote"
                useLegacySort: formId === 'edit-quote',
            });
            const binds = (0, extract_data_1.collectAllBindRefs)({
                bindIndexes,
                allBinds,
            });
            (0, functional_1.pipe)(models)((m) => (0, update_model_1.updateViaRef)({
                models: m,
                value,
                modelRef,
                normalizer,
                repeatIndex,
            }), (0, handle_action_1.default)({
                actions: updates,
                binds: allBinds[formIndex],
                repeatIndex,
            }), (0, binds_1.default)({ binds }));
            return;
        }
        case constants_1.RUN_UPDATES: {
            reporter_1.default.log('===========================running updates===========================');
            const { formId, binds } = action;
            const models = draft[formId];
            // The pipe below passes the model through the following functions,
            // returning an updated model state with each pass, until the final
            // model state is returned
            if (!binds)
                return;
            (0, binds_1.default)({ binds })(models);
            return;
        }
        case constants_1.RUN_PRE_SUBMIT_ACTIONS: {
            reporter_1.default.log('===========================running pre-submit actions===========================');
            const { formId, updates = [], repeatIndex } = action;
            const models = draft[formId];
            (0, handle_action_1.default)({ actions: updates, repeatIndex })(models);
            return;
        }
        case constants_1.EXTERNAL_MODEL_UPDATE: {
            const { formId, data, elementInjections, allBinds = [] } = action;
            const models = draft[formId];
            const changedModelIdsPerModel = (0, set_data_1.setModelData)({
                models,
                data,
                elementInjections,
                formId,
            });
            // We tried to be smart and run the set values for every model item we updated.
            // This doesn't work correctly because it needs to happen sychronously.
            // For example: modelItem value changes, set-values run, modelItem values changes, set-values run.
            // Instead of: update all the modelItem values, run all the set-values to all the modelItems that had changed.
            // Since this is just an enhancement, we can support this in a later version.
            // const updatesPerForm = changedModelIdsPerModel.map(formModelIds =>
            //   extractUpdates({
            //     modelIds: formModelIds,
            //     models,
            //   })
            // );
            // const updatesModelIds = changedModelIdsPerModel.reduce(
            //   (acc, modelIds, index) => {
            //     acc.concat(
            //       extractModelIdsFromActions({
            //         models,
            //         binds: allBinds[index],
            //         actions: updatesPerForm[index],
            //       })
            //     );
            //     return acc;
            //   },
            //   []
            // );
            const bindIndexes = (0, extract_data_1.extractBindIndexes)({
                // modelIds: flatten([...changedModelIdsPerModel, updatesModelIds]),
                modelIds: (0, functional_1.flatten)(changedModelIdsPerModel),
                models,
                // We need to use legacy sort for binding products
                // Agent Portal uses the formId of "edit-quote"
                useLegacySort: formId === 'edit-quote',
            });
            const binds = (0, extract_data_1.collectAllBindRefs)({
                bindIndexes,
                allBinds,
            });
            // updatesPerForm.forEach((updates, index) => {
            //   executeActions({ actions: updates, binds: allBinds[index] })(models);
            // });
            (0, binds_1.default)({ binds })(models);
            return;
        }
        case constants_1.TRIGGER_EVENT: {
            (0, handle_action_1.default)(action)(draft[action.formId]);
            return;
        }
        case constants_1.RESET_CHILD_FORM: {
            const { formId, formIndex } = action;
            (0, set_data_1.resetData)({ model: draft[formId][formIndex], models: draft[formId] });
            return;
        }
        // RESET_FIELDS is different than RESET_CHILD_FORM in that the default
        // value specified by the form is used rather than the initial value
        // which is the default values plus the initialData sent with loadNewForm
        case constants_1.RESET_FIELDS: {
            const { formId, formIndex, fields } = action;
            (0, set_data_1.resetData)({
                model: draft[formId][formIndex],
                list: fields,
                metaField: 'defaultValue',
                models: draft[formId],
            });
            return;
        }
        case constants_1.REMOVE_FORM: {
            delete draft[action.id];
        }
    }
};
exports.default = (0, immer_1.produce)(modelReducer, {});
//# sourceMappingURL=models.js.map